import React, {useState} from "react"
// MUI
import {Box, IconButton, Tooltip, Typography} from "@mui/material"
import {Delete, Edit, Star, StarBorder, Visibility} from "@mui/icons-material"
// Misc
import {languageCodeToName} from "@/utils"
// Layout / tokens
import {Badge} from "@/components/Layout"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import AppEditorTranslationsDialog
    from "@/components/Apps/AppsEditor/AppEditorTranslationsDialog/AppEditorTranslationsDialog"

// Translations manager: one row per language with inline actions
// (edit / preview / make default / delete). Rendered flush inside its card.
export default function AppEditorTranslationsSection(props: any) {

    const {
        app, setApp,
        previewTranslation, setPreviewTranslation,
        edition, setEdition,
        t,
    } = props

    const handleDeleteTranslation = (translation: any) => {
        if (!app.translations) return
        setApp({...app, translations: app.translations.filter((trans: any) => trans !== translation)})
    }

    const handleDefaultTranslation = (translation: any) => {
        if (!app.translations) return
        setApp({
            ...app,
            translations: app.translations.map((trans: any) => ({...trans, default: trans === translation})),
        })
    }

    const translations = app.translations ?? []

    return (
        <>
            {translations.length === 0 ? (
                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, textAlign: "center", py: 4}}>
                    {t("no_translations_yet")}
                </Typography>
            ) : translations.map((translation: any, index: number) => {
                const isPreviewed = previewTranslation && (previewTranslation.id ? previewTranslation.id === translation.id : previewTranslation === translation)
                return (
                    <Box
                        key={translation.id || index}
                        sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            px: 2.5, py: 1.25,
                            borderBottom: index < translations.length - 1 ? `1px solid ${tk.color.borderLight}` : "none",
                            transition: "background .15s ease",
                            "&:hover": {backgroundColor: tk.color.bg},
                        }}
                    >
                        <Typography sx={{fontSize: "13.5px", fontWeight: 600, color: tk.color.textPrimary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                            {languageCodeToName(translation.language)}
                        </Typography>
                        {translation.default && <Badge tone={"green"} dot>{t("default")}</Badge>}
                        <Box sx={{flex: 1}} />

                        <Tooltip title={t("common:edit")} placement={"top"} arrow>
                            <IconButton size={"small"} onClick={() => setEdition(translation)} aria-label={t("common:edit")} sx={{color: tk.color.textSecondary}}>
                                <Edit sx={{fontSize: 16}} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("preview_translation")} placement={"top"} arrow>
                            <span>
                                <IconButton
                                    size={"small"}
                                    onClick={() => setPreviewTranslation(translation)}
                                    aria-label={t("preview_translation")}
                                    disabled={Boolean(isPreviewed)}
                                    sx={{color: tk.color.textSecondary}}
                                >
                                    <Visibility sx={{fontSize: 16}} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("make_translation_default")} placement={"top"} arrow>
                            <span>
                                <IconButton
                                    size={"small"}
                                    onClick={() => handleDefaultTranslation(translation)}
                                    aria-label={t("make_translation_default")}
                                    disabled={translation.default}
                                    sx={{color: translation.default ? tk.color.amber : tk.color.textSecondary}}
                                >
                                    {translation.default ? <Star sx={{fontSize: 16}} /> : <StarBorder sx={{fontSize: 16}} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("common:delete")} placement={"top"} arrow>
                            <span>
                                <IconButton
                                    size={"small"}
                                    onClick={() => handleDeleteTranslation(translation)}
                                    aria-label={t("common:delete")}
                                    disabled={translation.default}
                                    sx={{color: tk.color.red}}
                                >
                                    <Delete sx={{fontSize: 16}} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )
            })}

            <AppEditorTranslationsDialog
                open={Boolean(edition)}
                onClose={() => setEdition(null)}
                translation={edition}
                {...{app, setApp, t}}
            />
        </>
    )
}
